/**
 * ZIP File Handler - Extract and manage multi-file OpenSCAD projects
 * @license GPL-3.0-or-later
 */

import { hasParentSegment } from './file-param-resolver.js';
import { withoutComments } from './scad-comments.js';
import { includeStatements } from './scad-directives.js';

/**
 * Extract files from a ZIP archive
 * @param {File|Blob} zipFile - ZIP file to extract
 * @returns {Promise<{files: Map<string, string>, mainFile: string}>}
 */
export async function extractZipFiles(zipFile) {
  try {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const zipData = await zip.loadAsync(zipFile);

    const files = new Map();
    let mainFile = null;
    const scadFiles = [];

    // Image file types: extracted as base64 data URLs so the preview layer
    // and THREE.js overlay renderer can consume them directly.
    const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);

    // Binary meshes import() reads: kept byte for byte as data URLs, which
    // the worker decodes when it mounts them.
    const MESH_EXTS = new Set(['stl', '3mf', 'obj', 'amf']);

    // Binary types nothing reads. Extracting these as UTF-8 text would
    // corrupt their content and waste significant memory.
    const BINARY_ONLY_EXTS = new Set([
      'bmp',
      'tiff',
      'tif',
      'ico',
      'wrl',
      'zip',
      'gz',
      'tar',
      'mp4',
      'mp3',
      'wav',
      'avi',
      'mov',
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
    ]);

    // Extract all files
    for (const [relativePath, zipEntry] of Object.entries(zipData.files)) {
      // Skip directories
      if (zipEntry.dir) continue;

      // Normalize path early so it is available for all extraction branches
      const normalizedPath = relativePath
        .replace(/^\/+/, '')
        .replace(/\\/g, '/');

      // Security: Reject paths with directory traversal attempts
      if (
        hasParentSegment(normalizedPath) ||
        normalizedPath.startsWith('/') ||
        normalizedPath.includes('\\')
      ) {
        console.warn(
          `[ZIP] Skipping potentially malicious path: ${relativePath}`
        );
        continue;
      }

      const extMatch = normalizedPath.match(/\.([^./\\]+)$/);
      const ext = extMatch ? extMatch[1].toLowerCase() : '';

      // Extract image files as base64 data URLs (binary-safe)
      if (IMAGE_EXTS.has(ext)) {
        const base64Data = await zipEntry.async('base64');
        const mime = ext === 'jpg' ? 'jpeg' : ext;
        files.set(normalizedPath, `data:image/${mime};base64,${base64Data}`);
        console.log(
          `[ZIP] Extracted image: ${normalizedPath} (${base64Data.length} base64 chars)`
        );
        continue;
      }

      if (MESH_EXTS.has(ext)) {
        const base64Data = await zipEntry.async('base64');
        files.set(
          normalizedPath,
          `data:application/octet-stream;base64,${base64Data}`
        );
        continue;
      }

      // Skip binary files nothing reads
      if (BINARY_ONLY_EXTS.has(ext)) {
        console.log(
          `[ZIP] Skipping binary file (not usable as text): ${relativePath}`
        );
        continue;
      }

      // Extract file content as text
      const content = await zipEntry.async('text');
      files.set(normalizedPath, content);

      // Track .scad files for main file detection
      if (normalizedPath.endsWith('.scad')) {
        scadFiles.push(normalizedPath);
      }

      console.log(
        `[ZIP] Extracted: ${normalizedPath} (${content.length} bytes)`
      );
    }

    // Detect main file
    mainFile = detectMainFile(scadFiles, files);

    if (!mainFile) {
      throw new Error('No .scad files found in ZIP archive');
    }

    console.log(`[ZIP] Main file detected: ${mainFile}`);
    console.log(`[ZIP] Total files extracted: ${files.size}`);

    // Diagnostic: classify each extracted file by type and size
    const fileClassification = {};
    for (const [filePath, content] of files.entries()) {
      const ext = filePath.split('.').pop()?.toLowerCase() || 'unknown';
      if (!fileClassification[ext])
        fileClassification[ext] = { count: 0, totalBytes: 0 };
      fileClassification[ext].count++;
      fileClassification[ext].totalBytes += content.length;
    }
    console.debug('[ZIP] File classification:', fileClassification);

    return { files, mainFile };
  } catch (error) {
    console.error('[ZIP] Extraction failed:', error);
    throw new Error(`Failed to extract ZIP file: ${error.message}`);
  }
}

/**
 * Detect the main .scad file from a list of candidates
 * Strategy:
 * 1. Look for "main.scad" or files with "main" in the filename
 * 2. Look for files in the root directory (no subdirectories)
 * 3. Look for the first .scad file with Customizer annotations
 * 4. Fall back to the first .scad file alphabetically
 *
 * @param {string[]} scadFiles - List of .scad file paths
 * @param {Map<string, string>} files - All extracted files
 * @returns {string|null} - Path to main file
 */
export function detectMainFile(scadFiles, files) {
  if (scadFiles.length === 0) return null;
  if (scadFiles.length === 1) return scadFiles[0];

  // Strategy 1: Look for "main.scad"
  const mainScad = scadFiles.find(
    (path) =>
      path.toLowerCase() === 'main.scad' ||
      path.toLowerCase().endsWith('/main.scad')
  );
  if (mainScad) return mainScad;

  // Strategy 2: Look for files with "main" in the filename (basename only)
  const mainNamed = scadFiles.find((path) =>
    path.split('/').pop().toLowerCase().includes('main')
  );
  if (mainNamed) return mainNamed;

  // Strategy 3: Prefer root directory files
  const rootFiles = scadFiles.filter((path) => !path.includes('/'));
  if (rootFiles.length === 1) return rootFiles[0];

  // Strategy 4: Look for Customizer annotations
  for (const path of rootFiles.length > 0 ? rootFiles : scadFiles) {
    const content = files.get(path);
    if (content && hasCustomizerAnnotations(content)) {
      return path;
    }
  }

  // Strategy 5: Fall back to first file (prefer root, then alphabetical)
  return rootFiles.length > 0 ? rootFiles.sort()[0] : scadFiles.sort()[0];
}

/**
 * Check if a .scad file contains Customizer annotations
 * @param {string} content - File content
 * @returns {boolean}
 */
export function hasCustomizerAnnotations(content) {
  // Look for common Customizer patterns
  const patterns = [
    /\/\*\s*\[.*?\]\s*\*\//, // /*[Group]*/
    /\/\/\s*\[.*?\]/, // // [min:max] or // [opt1, opt2]
  ];

  return patterns.some((pattern) => pattern.test(content));
}

/**
 * Validate ZIP file before extraction
 * @param {File} file - File to validate
 * @returns {{valid: boolean, error?: string}}
 */
import { FILE_SIZE_LIMITS } from './validation-constants.js';

export function validateZipFile(file) {
  // Check file extension
  if (!file.name.toLowerCase().endsWith('.zip')) {
    return { valid: false, error: 'File must have .zip extension' };
  }

  // Check file size using shared constant
  if (file.size > FILE_SIZE_LIMITS.ZIP_FILE) {
    const limitMB = FILE_SIZE_LIMITS.ZIP_FILE / (1024 * 1024);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `ZIP file exceeds ${limitMB}MB limit (${fileSizeMB}MB)`,
    };
  }

  // Check for empty file
  if (file.size === 0) {
    return { valid: false, error: 'ZIP file is empty' };
  }

  return { valid: true };
}

/**
 * Build a recursive nested tree from a flat Map<path, content>.
 *
 * Each node has the shape:
 *   { folders: Map<string, node>, files: [{ path, name, content }] }
 *
 * Paths are forward-slash delimited. Leading slashes are stripped.
 * Sentinel `.folder` entries (used to represent empty folders) are excluded
 * from the files array but their parent folder node is still created.
 *
 * @param {Map<string, string>} fileMap - Flat path → content map
 * @returns {{ folders: Map<string, object>, files: Array<{path:string, name:string, content:string}> }}
 */
export function buildNestedTree(fileMap) {
  const root = { folders: new Map(), files: [] };

  for (const [rawPath, content] of fileMap.entries()) {
    const path = rawPath.replace(/^\/+/, '');
    const parts = path.split('/');
    let node = root;

    // Walk / create intermediate folder nodes
    for (let i = 0; i < parts.length - 1; i++) {
      const segment = parts[i];
      if (!node.folders.has(segment)) {
        node.folders.set(segment, { folders: new Map(), files: [] });
      }
      node = node.folders.get(segment);
    }

    const filename = parts[parts.length - 1];
    // Skip hidden sentinel entries used to represent empty folders
    if (filename !== '.folder') {
      node.files.push({ path, name: filename, content });
    }
  }

  return root;
}

/**
 * Retrieve the subtree node at a given path (array of folder name segments).
 * Returns the root node when pathSegments is empty.
 * Returns null if any segment along the path does not exist.
 *
 * @param {{ folders: Map<string, object>, files: Array }} tree - Root node
 * @param {string[]} pathSegments - Ordered folder names, e.g. ['Cases', 'iPad 7,8,9']
 * @returns {{ folders: Map<string, object>, files: Array } | null}
 */
export function getNodeAtPath(tree, pathSegments) {
  let node = tree;
  for (const segment of pathSegments) {
    if (!node.folders.has(segment)) return null;
    node = node.folders.get(segment);
  }
  return node;
}

/**
 * Count all files recursively under a tree node (excluding sentinel .folder entries,
 * which are already excluded during buildNestedTree).
 *
 * @param {{ folders: Map<string, object>, files: Array }} node
 * @returns {number}
 */
export function countFilesRecursive(node) {
  let count = node.files.length;
  for (const child of node.folders.values()) {
    count += countFilesRecursive(child);
  }
  return count;
}

/**
 * Resolve include/use paths relative to a base file
 * @param {string} statement - include/use statement (e.g., 'include <utils/helpers.scad>')
 * @param {string} currentFile - Path of file containing the statement
 * @returns {string} - Resolved absolute path
 */
export function resolveIncludePath(statement, currentFile) {
  // Extract path from include/use statement
  // Matches: include <path>, include "path", use <path>, use "path"
  const match = statement.match(/(?:include|use)\s*[<"]([^>"]+)[>"]/);
  if (!match) return null;

  const includePath = match[1];

  // If absolute path (starts with /), use as-is
  if (includePath.startsWith('/')) {
    return includePath.slice(1); // Remove leading slash
  }

  // Resolve relative to current file's directory
  const currentDir = currentFile.includes('/')
    ? currentFile.substring(0, currentFile.lastIndexOf('/'))
    : '';

  if (!currentDir) {
    // Current file is in root, so relative path is the include path
    return includePath;
  }

  // Join paths and normalize
  const resolved = currentDir + '/' + includePath;
  return normalizePath(resolved);
}

/**
 * Normalize a file path (resolve .. and .)
 * @param {string} path
 * @returns {string}
 */
function normalizePath(path) {
  const parts = path.split('/');
  const result = [];

  for (const part of parts) {
    if (part === '.' || part === '') continue;
    if (part === '..') {
      result.pop();
    } else {
      result.push(part);
    }
  }

  return result.join('/');
}

/**
 * Scan a .scad file for include/use statements
 * @param {string} content - File content
 * @returns {string[]} - Array of include/use statements
 */
export function scanIncludes(content) {
  return includeStatements(withoutComments(content)).map(
    ({ kind, path }) => `${kind} <${path}>`
  );
}

/**
 * Resolve a file from projectFiles by exact key or basename fallback.
 * Pure function with no side effects — safe to unit-test in isolation.
 *
 * Resolution order:
 *  1. Exact key match (returns immediately)
 *  2. Basename match (segment after last '/') — succeeds only when exactly
 *     one file in the Map has that basename; returns null when ambiguous.
 *
 * @param {Map<string, string>} projectFiles
 * @param {string} filename - Exact key or bare filename to resolve
 * @returns {{ key: string, content: string } | null}
 */
export function resolveProjectFile(projectFiles, filename) {
  if (!projectFiles || !filename) return null;

  if (projectFiles.has(filename)) {
    return { key: filename, content: projectFiles.get(filename) };
  }

  const matches = [];
  for (const key of projectFiles.keys()) {
    const basename = key.includes('/') ? key.split('/').pop() : key;
    if (basename === filename) matches.push(key);
  }

  if (matches.length === 1) {
    return { key: matches[0], content: projectFiles.get(matches[0]) };
  }

  if (matches.length > 1) {
    console.warn(
      `[resolveProjectFile] "${filename}" matches ${matches.length} paths — ` +
        'cannot auto-select. Require explicit user selection or preset mapping.'
    );
  }

  return null;
}

/**
 * Check whether a brand name matches a folder name that may include
 * the "-equivalent Case" suffix and/or inconsistent whitespace.
 * Both sides are whitespace-collapsed and lowercased before comparison.
 *
 * @param {string} folderName - Folder segment, e.g. "SP LTROP-equivalent Case"
 * @param {string} brand - Brand extracted from preset name, e.g. "SP LTROP"
 * @returns {boolean}
 */
export function matchesBrand(folderName, brand) {
  const norm = (s) =>
    s
      .replace(/-equivalent\s*case/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  return norm(folderName) === norm(brand);
}

/**
 * Parse a structured preset name into its component parts.
 * Expected format: "<tablet> - <brand> - <app>" using " - " as separator.
 * Only the first two separators are used; any additional " - " in the
 * app portion is preserved as-is.
 *
 * @param {string} name - Preset name, e.g. "iPad 10,11 - Andnary - LWFL"
 * @returns {{ tablet: string, brand: string, app: string|null } | null}
 */
export function parsePresetParts(name) {
  const idx1 = name.indexOf(' - ');
  if (idx1 === -1) return null;
  const tablet = name.substring(0, idx1);
  const rest = name.substring(idx1 + 3);
  const idx2 = rest.indexOf(' - ');
  if (idx2 === -1) {
    return { tablet, brand: rest, app: null };
  }
  return {
    tablet,
    brand: rest.substring(0, idx2),
    app: rest.substring(idx2 + 3) || null,
  };
}

/**
 * Build a runtime mapping from preset names to their specific companion file
 * paths inside the projectFiles Map. Uses token-scoring heuristics so that
 * e.g. preset "iPad 7,8,9 - Fintie - TouchChat" maps to
 * "Cases and App Specifics/iPad 7,8,9/Fintie/TouchChat/openings_and_additions.txt".
 *
 * Only basenames that appear at MULTIPLE paths (aliasable) are considered for
 * the openings file mapping. Single-instance files need no alias.
 *
 * Returns path references only — no file content is duplicated.
 *
 * @param {Map<string, string>} files - All project files from ZIP extraction
 * @param {Object} parameterSets - OpenSCAD parameterSets object { "preset name": {...} }
 * @returns {Map<string, { openingsPath: string|null, svgPath: string|null }>}
 */
export function buildPresetCompanionMap(files, parameterSets, options = {}) {
  const result = new Map();
  if (!files || !parameterSets) return result;

  const presetNames = Object.keys(parameterSets).filter(
    (n) => n !== 'design default values'
  );
  if (presetNames.length === 0) return result;

  const { companionTargets } = options;
  const useGenericPath =
    Array.isArray(companionTargets) && companionTargets.length > 0;

  // Group all file paths by basename
  const byBasename = new Map();
  for (const key of files.keys()) {
    const basename = key.split('/').pop();
    if (!byBasename.has(basename)) byBasename.set(basename, []);
    byBasename.get(basename).push(key);
  }

  // Aliasable basenames: same filename appears under multiple paths
  const aliasableBasenames = new Map();
  for (const [basename, paths] of byBasename.entries()) {
    if (paths.length > 1) aliasableBasenames.set(basename, paths);
  }

  // All SVG paths in the project
  const svgPaths = Array.from(files.keys()).filter((k) =>
    k.toLowerCase().endsWith('.svg')
  );

  // Tokenise a preset name into meaningful lowercase substrings.
  // Single-digit numeric tokens (e.g. "7", "8", "9" from "iPad 7,8,9") are kept
  // because they disambiguate tablet-version folder names from each other.
  function tokenise(name) {
    return name
      .toLowerCase()
      .replace(/coughdrop/g, 'cough drop')
      .replace(/vocochat/g, 'voco chat')
      .split(/[\s\-_/,().]+/)
      .filter((t) => t.length > 1 || /^\d$/.test(t));
  }

  // Word-boundary matching: tokens must appear as whole words in folder
  // segments, preventing false positives like "sp" matching "specifics".
  function scorePath(path, tokens) {
    const lower = path
      .toLowerCase()
      .replace(/-equivalent\s*case/g, '')
      .replace(/coughdrop/g, 'cough drop')
      .replace(/vocochat/g, 'voco chat');
    const folderWords = new Set(
      lower
        .split('/')
        .slice(0, -1)
        .flatMap((seg) => seg.split(/[\s,\-_().]+/))
        .filter(Boolean)
    );
    return tokens.filter((t) => folderWords.has(t)).length;
  }

  function pickBest(candidates, tokens, parts = null) {
    if (!candidates || candidates.length === 0)
      return { path: null, resolution: 'ambiguous' };
    if (candidates.length === 1) {
      return scorePath(candidates[0], tokens) > 0
        ? { path: candidates[0], resolution: 'unique' }
        : { path: null, resolution: 'ambiguous' };
    }
    const scored = candidates
      .map((p) => ({ path: p, score: scorePath(p, tokens) }))
      .sort(
        (a, b) =>
          b.score - a.score ||
          a.path.split('/').length - b.path.split('/').length
      );
    if (scored[0].score === 0) return { path: null, resolution: 'ambiguous' };
    return resolveByHierarchy(scored, tokens, parts);
  }

  // When candidates share parent-child relationships, resolve using
  // directory hierarchy rather than raw token overlap scores.
  // Returns { path: string|null, resolution: 'unique'|'ancestor-fallback'|'ambiguous' }.
  function resolveByHierarchy(scored, tokens, parts = null) {
    const topScore = scored[0].score;
    const tied = scored.filter((s) => s.score === topScore);

    if (tied.length === 1) {
      const winner = tied[0];
      const parent = findAncestorCandidate(winner, scored);
      if (parent && parent.score > 0) {
        if (!extraSegmentsMatchTokens(parent.path, winner.path, tokens)) {
          return { path: parent.path, resolution: 'ancestor-fallback' };
        }
      }
      return { path: winner.path, resolution: 'unique' };
    }

    // Among tied candidates, return the one that is an ancestor of all others
    const ancestor = tied.find((a) => {
      const aDir = a.path.substring(0, a.path.lastIndexOf('/'));
      if (!aDir) return false;
      return tied.every(
        (b) =>
          b === a ||
          b.path.substring(0, b.path.lastIndexOf('/')).startsWith(aDir + '/')
      );
    });
    if (ancestor)
      return { path: ancestor.path, resolution: 'ancestor-fallback' };

    // Sibling tie — filter by extra-segment token matching.
    // Compute the common ancestor directory of all tied candidates, then
    // keep only those whose extra folder words all appear in the preset tokens.
    const dirs = tied.map((s) =>
      s.path.substring(0, s.path.lastIndexOf('/')).split('/')
    );
    let commonLen = 0;
    for (let i = 0; i < dirs[0].length; i++) {
      if (dirs.every((d) => i < d.length && d[i] === dirs[0][i])) {
        commonLen = i + 1;
      } else {
        break;
      }
    }
    const commonDir = dirs[0].slice(0, commonLen).join('/');
    if (commonDir) {
      const basename = tied[0].path.split('/').pop();
      const syntheticParent = commonDir + '/' + basename;
      const matched = tied.filter((s) =>
        extraSegmentsMatchTokens(syntheticParent, s.path, tokens)
      );
      if (matched.length === 1)
        return { path: matched[0].path, resolution: 'unique' };
      if (matched.length > 1) {
        matched.sort(
          (a, b) => a.path.split('/').length - b.path.split('/').length
        );
        return { path: matched[0].path, resolution: 'unique' };
      }
    }

    // App-name exact-match tie-breaker: when the preset has a parsed
    // app name, prefer candidates whose leaf folder tokenizes to the
    // same set as the app name. This disambiguates e.g. LWFL vs LWFL-VI
    // siblings and picks the correct app-level file over a mount-type
    // ancestor when mount-type info is absent from the preset name.
    if (parts?.app) {
      const appTokenSet = new Set(tokenise(parts.app));
      const leafMatched = tied.filter((s) => {
        const dir = s.path.substring(0, s.path.lastIndexOf('/'));
        const leaf = dir.split('/').pop();
        const leafTokenSet = new Set(tokenise(leaf));
        if (leafTokenSet.size !== appTokenSet.size) return false;
        for (const t of leafTokenSet) {
          if (!appTokenSet.has(t)) return false;
        }
        return true;
      });
      if (leafMatched.length > 0) {
        leafMatched.sort(
          (a, b) =>
            a.path.split('/').length - b.path.split('/').length ||
            a.path.localeCompare(b.path)
        );
        return { path: leafMatched[0].path, resolution: 'unique' };
      }
    }

    // Heuristic default: when sibling ties can't be resolved by
    // extra-segment or app-name matching, fall back to the shallowest
    // scored ancestor of any tied candidate (e.g. mount-type-level
    // file when app-level candidates are ambiguous under LTROP's
    // 3-level hierarchy and the preset app name doesn't match any
    // leaf folder).
    const ancestorFallbacks = scored.filter((s) => {
      if (s.score === 0 || tied.includes(s)) return false;
      const sDir = s.path.substring(0, s.path.lastIndexOf('/'));
      if (!sDir) return false;
      return tied.some((t) => {
        const tDir = t.path.substring(0, t.path.lastIndexOf('/'));
        return tDir.startsWith(sDir + '/');
      });
    });
    if (ancestorFallbacks.length > 0) {
      ancestorFallbacks.sort(
        (a, b) =>
          a.path.split('/').length - b.path.split('/').length ||
          a.path.localeCompare(b.path)
      );
      return {
        path: ancestorFallbacks[0].path,
        resolution: 'ancestor-fallback',
      };
    }

    return { path: null, resolution: 'ambiguous' };
  }

  // Find the nearest (deepest) scored candidate whose directory is a
  // proper ancestor of the winner's directory.
  function findAncestorCandidate(winner, scored) {
    const winnerDir = winner.path.substring(0, winner.path.lastIndexOf('/'));
    if (!winnerDir) return null;

    let best = null;
    let bestDirLen = -1;
    for (const s of scored) {
      if (s === winner || s.score === 0) continue;
      const sDir = s.path.substring(0, s.path.lastIndexOf('/'));
      if (!sDir) continue;
      if (winnerDir.startsWith(sDir + '/') && sDir.length > bestDirLen) {
        best = s;
        bestDirLen = sDir.length;
      }
    }
    return best;
  }

  // Check whether every word in the child-specific folder segments
  // appears in the preset tokens. Uses the same normalization as
  // scorePath so that e.g. "-equivalent Case" is stripped.
  function extraSegmentsMatchTokens(parentPath, childPath, tokens) {
    const parentDir = parentPath.substring(0, parentPath.lastIndexOf('/'));
    const childDir = childPath.substring(0, childPath.lastIndexOf('/'));
    const extra = childDir.substring(parentDir.length + 1);
    const words = extra
      .toLowerCase()
      .replace(/-equivalent\s*case/g, '')
      .replace(/coughdrop/g, 'cough drop')
      .replace(/vocochat/g, 'voco chat')
      .split(/[\s,\-_/().]+/)
      .filter((w) => w.length > 1 || /^\d$/.test(w));
    if (words.length === 0) return true;
    return words.every((w) => tokens.includes(w));
  }

  function filterByBrand(candidates, brand) {
    if (!brand) return candidates;
    const filtered = candidates.filter((path) => {
      const segments = path.split('/').slice(0, -1);
      return segments.some((seg) => matchesBrand(seg, brand));
    });
    return filtered.length > 0 ? filtered : candidates;
  }

  for (const presetName of presetNames) {
    const tokens = tokenise(presetName);
    const parts = parsePresetParts(presetName);
    const brand = parts ? parts.brand : null;

    if (useGenericPath) {
      const aliases = {};
      let entryResolution = 'unique';

      for (const target of companionTargets) {
        const candidates = aliasableBasenames.get(target);
        if (candidates) {
          const bestResult = pickBest(
            filterByBrand(candidates, brand),
            tokens,
            parts
          );
          if (bestResult.path) {
            aliases[target] = bestResult.path;
            if (
              bestResult.resolution === 'ancestor-fallback' &&
              entryResolution === 'unique'
            ) {
              entryResolution = 'ancestor-fallback';
            }
          } else {
            entryResolution = 'ambiguous';
            console.warn(
              `[PresetCompanionMap] Cannot unambiguously resolve "${target}" for preset: "${presetName}"`
            );
          }
        }
      }

      let svgAliasTarget = null;
      if (svgPaths.length === 1) {
        const basename = svgPaths[0].split('/').pop();
        aliases[basename] = svgPaths[0];
        svgAliasTarget = basename;
      } else if (svgPaths.length > 1) {
        const svgResult = pickBest(
          filterByBrand(svgPaths, brand),
          tokens,
          parts
        );
        if (svgResult.path) {
          const basename = svgResult.path.split('/').pop();
          aliases[basename] = svgResult.path;
          svgAliasTarget = basename;
        }
      }

      result.set(presetName, {
        aliases,
        svgAliasTarget,
        resolution: entryResolution,
      });
    } else {
      // Legacy-only compatibility path: keyguard-shaped fallback mapping for
      // archives that do not expose explicit companion metadata.
      let openingsPath = null;
      let entryResolution = 'unique';
      const openingsCandidates = aliasableBasenames.get(
        'openings_and_additions.txt'
      );
      if (openingsCandidates) {
        const openingsResult = pickBest(
          filterByBrand(openingsCandidates, brand),
          tokens,
          parts
        );
        openingsPath = openingsResult.path;
        if (!openingsPath) {
          entryResolution = 'ambiguous';
          console.warn(
            `[PresetCompanionMap] Cannot unambiguously resolve openings path for preset: "${presetName}"`
          );
        } else if (openingsResult.resolution === 'ancestor-fallback') {
          entryResolution = 'ancestor-fallback';
        }
      }

      let svgPath = null;
      if (svgPaths.length === 1) {
        svgPath = svgPaths[0];
      } else if (svgPaths.length > 1) {
        const svgResult = pickBest(
          filterByBrand(svgPaths, brand),
          tokens,
          parts
        );
        svgPath = svgResult.path;
        if (!svgPath) {
          console.warn(
            `[PresetCompanionMap] Cannot unambiguously resolve SVG path for preset: "${presetName}"`
          );
        }
      }

      result.set(presetName, {
        openingsPath,
        svgPath,
        resolution: entryResolution,
      });
    }
  }

  const diagCounts = { unique: 0, 'ancestor-fallback': 0, ambiguous: 0 };
  for (const entry of result.values()) {
    const r = entry.resolution || 'unique';
    diagCounts[r] = (diagCounts[r] || 0) + 1;
  }

  if (useGenericPath) {
    const mappedCount = Array.from(result.values()).filter(
      (v) => Object.keys(v.aliases || {}).length > 0
    ).length;
    console.log(
      `[PresetCompanionMap] Mapped ${mappedCount}/${presetNames.length} presets to companion aliases`
    );
  } else {
    const mappedCount = Array.from(result.values()).filter(
      (v) => v.openingsPath !== null
    ).length;
    console.log(
      `[PresetCompanionMap] Mapped ${mappedCount}/${presetNames.length} presets via legacy openings fallback`
    );
  }

  console.debug(
    `[PresetCompanionMap] Resolution: ${diagCounts.unique} unique, ` +
      `${diagCounts['ancestor-fallback']} ancestor-fallback, ` +
      `${diagCounts.ambiguous} ambiguous`
  );
  if (diagCounts['ancestor-fallback'] > 0) {
    const fallbackNames = [...result.entries()]
      .filter(([, v]) => v.resolution === 'ancestor-fallback')
      .map(([n]) => n);
    console.debug(
      '[PresetCompanionMap] Ancestor-fallback presets:',
      fallbackNames
    );
  }
  if (diagCounts.ambiguous > 0) {
    const ambiguousNames = [...result.entries()]
      .filter(([, v]) => v.resolution === 'ambiguous')
      .map(([n]) => n);
    console.warn('[PresetCompanionMap] Ambiguous presets:', ambiguousNames);
  }

  return result;
}

/**
 * Apply companion file alias mounting to a projectFiles Map.
 * Copies preset-specific file content from nested paths to the root-level
 * keys that SCAD include/import statements resolve to.
 *
 * Pure function — returns a new Map without mutating the input.
 *
 * @param {Map<string, string>} projectFiles
 * @param {{ openingsPath: string|null, svgPath: string|null }|null} companionMapping
 * @returns {Map<string, string>}
 */
export function applyCompanionAliases(projectFiles, companionMapping) {
  const result = new Map(projectFiles);
  if (!companionMapping) return result;

  if (
    companionMapping.aliases &&
    typeof companionMapping.aliases === 'object'
  ) {
    for (const [aliasTarget, sourcePath] of Object.entries(
      companionMapping.aliases
    )) {
      if (sourcePath && result.has(sourcePath) && !result.has(aliasTarget)) {
        console.debug(
          `[CompanionAlias] Creating "${aliasTarget}" from "${sourcePath}" (target was missing)`
        );
        result.set(aliasTarget, result.get(sourcePath));
      } else if (
        sourcePath &&
        result.has(sourcePath) &&
        result.has(aliasTarget)
      ) {
        console.debug(
          `[CompanionAlias] Skipping "${aliasTarget}" — root key already exists, preserving original`
        );
      }
    }
    return result;
  }

  // Legacy-only compatibility path: keyguard-shaped mapping copies
  // preset-specific content to hardcoded root-level keys
  // "openings_and_additions.txt" / "default.svg". Guard: only create a
  // missing root key; replacing an existing one would change the content
  // SCAD include/import directives resolve to, producing wrong geometry.
  if (
    companionMapping.openingsPath &&
    result.has(companionMapping.openingsPath) &&
    !result.has('openings_and_additions.txt')
  ) {
    console.debug(
      `[CompanionAlias] Creating "openings_and_additions.txt" from "${companionMapping.openingsPath}" (legacy path)`
    );
    result.set(
      'openings_and_additions.txt',
      result.get(companionMapping.openingsPath)
    );
  }
  if (
    companionMapping.svgPath &&
    result.has(companionMapping.svgPath) &&
    !result.has('default.svg')
  ) {
    console.debug(
      `[CompanionAlias] Creating "default.svg" from "${companionMapping.svgPath}" (legacy path)`
    );
    result.set('default.svg', result.get(companionMapping.svgPath));
  }

  return result;
}

/**
 * Extract the SVG overlay alias target from a companion mapping.
 * Works with both generic and legacy mapping formats.
 *
 * @param {{ aliases?: Object, svgAliasTarget?: string|null, svgPath?: string|null }|null} companionMapping
 * @returns {string|null} The file key to use for loading the overlay SVG
 */
export function getOverlaySvgTarget(companionMapping) {
  if (!companionMapping) return null;
  if (companionMapping.aliases) {
    if (companionMapping.svgAliasTarget) return companionMapping.svgAliasTarget;
    for (const key of Object.keys(companionMapping.aliases)) {
      if (key.toLowerCase().endsWith('.svg')) return key;
    }
    return null;
  }
  // Legacy compatibility target for keyguard packages.
  if (companionMapping.svgPath) return 'default.svg';
  return null;
}

/**
 * Find the first overlay-suitable asset (SVG or image) in a project files Map.
 * Prefers SVG files over raster images.
 *
 * @param {Map<string, string>} projectFiles
 * @returns {string|null} File key of the first overlay-suitable asset
 */
export function findFirstOverlayAsset(projectFiles) {
  if (!projectFiles || projectFiles.size === 0) return null;
  const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
  for (const key of projectFiles.keys()) {
    if (key.toLowerCase().endsWith('.svg')) return key;
  }
  for (const key of projectFiles.keys()) {
    if (IMAGE_EXTS.some((ext) => key.toLowerCase().endsWith(ext))) return key;
  }
  return null;
}

/**
 * Get file statistics for a ZIP project
 * @param {Map<string, string>} files - Extracted files
 * @returns {Object} - Statistics
 */
export function getZipStats(files) {
  const scadFiles = [];
  const otherFiles = [];
  let totalSize = 0;

  for (const [path, content] of files.entries()) {
    totalSize += content.length;

    if (path.endsWith('.scad')) {
      scadFiles.push(path);
    } else {
      otherFiles.push(path);
    }
  }

  return {
    totalFiles: files.size,
    scadFiles: scadFiles.length,
    otherFiles: otherFiles.length,
    totalSize,
    scadFilesList: scadFiles,
    otherFilesList: otherFiles,
  };
}
